/**
 * When the preview-first builder is REACHABLE while the bytes upload (ui#1814).
 *
 * Plain TypeScript — no React import. Whether a sender is invited to compose is a product rule, and
 * this repo keeps no UI-component tests, so a condition written inside the progress card would have no
 * coverage at all.
 */

import type {PersonalizationDraftError} from "./personalizationDraft";
import type {PersonalizationTemplateArity} from "./personalizationOffer";

export interface UploadBuilderInput {
	/** The bytes are moving: the progress card owns the canvas. */
	uploading: boolean;
	/**
	 * This send has a COMPLETION that can carry what is composed — `createFormLogic.usesHostedUpload`
	 * for the send in flight.
	 *
	 * Every hosted send qualifies since api#1110. A plaintext (recipients) send names its
	 * personalization on the bulk completion beside its files (api#1099); an encrypted shareable link
	 * completes its files one at a time through the multipart route and then sends the SAME bulk
	 * completion carrying the personalization and no files at all. Both reach one service call with one
	 * set of funding, write-once and owner rules.
	 *
	 * An INSTANT (peer-to-peer) send is the remaining false, and it is not a gap waiting on the api: it
	 * registers its metadata in a single call and has no completion to carry anything. Its
	 * personalization rides that call, exactly as it has since api#461 — as does a personalization
	 * composed before Send on either delivery.
	 */
	attachable: boolean;
	/** This sender may compose at all — signed in, on a brand that offers personalization. */
	composable: boolean;
}

/**
 * Whether the progress card offers its way into the builder.
 *
 * <b>A button the sender presses, never a sheet that appears on a timer</b> (owner 2026-09-02). The
 * flow used to raise the builder by itself once an upload looked long enough to have dead time in it,
 * which made the offer arrive differently on a fast link than on a slow one and took the screen from a
 * sender who had asked for nothing. One always-visible control replaces it: the same invitation, at the
 * sender's own moment, identical on a nine-second send and a nine-minute one.
 *
 * Every condition here is about whether composing could lead anywhere — not about timing, and not about
 * whether the builder has been opened before. A sender who composes, closes and wants another look must
 * find the way back exactly where it was.
 */
export function builderEntryOffered(input: UploadBuilderInput): boolean {
	return input.uploading && input.attachable && input.composable;
}

/** Which `sender.personalise` name the builder's logo tile carries — see {@link logoPickLabelKey}. */
export type LogoPickLabelKey = "chooseLogo" | "replaceLogo";

/**
 * The logo tile's accessible name (ui#1942, owner: "the click moved onto the logo actual UI").
 *
 * The tile IS the file picker now — there is no visible "Choose a logo" button beside it any more — so
 * this wording is the whole of what a screen-reader user is told the control does. That makes it copy a
 * rule chooses rather than a caption a label happens to carry, and this repo keeps no UI-component
 * tests, so a ternary in the tile's JSX would be the one user-visible string on that control with no
 * coverage at all.
 *
 * A KEY, not a string: the two names live in both catalogues and the rule must not know either
 * language.
 */
export function logoPickLabelKey(hasLogo: boolean): LogoPickLabelKey {
	return hasLogo ? "replaceLogo" : "chooseLogo";
}

/** Which `sender.personalise` name the colour row's first swatch carries — see {@link accentClearLabelKey}. */
export type AccentClearLabelKey = "noColor" | "accountColor";

/**
 * The accessible name of the colour row's first swatch, the one checked while this transfer sets no
 * colour of its own (review of kitecorp/transferify-ui#2418).
 *
 * What such a transfer GETS depends on the account: nothing, or the account's own accent, which the
 * recipient page then paints. "No colour" is only true for the first, so a sender with an account accent
 * hears that the swatch keeps it. A rule rather than a ternary in the sheet, for the reason
 * {@link logoPickLabelKey} is one: it is the whole of what a screen-reader user is told about that swatch.
 */
export function accentClearLabelKey(inheritedAccent: string | null): AccentClearLabelKey {
	return inheritedAccent === null ? "noColor" : "accountColor";
}

/**
 * What the ONE line under the animation tiles says (kitecorp/transferify-ui#2416).
 *
 * - `invitation` — nothing chosen yet, so the line invites a choice.
 * - `required` — an animation is chosen and its floor is unmet. The pictures are MANDATORY from here
 *   on, and this is the only place the sender is told so: they choose the animation at this control
 *   and may never scroll as far as the images section, which is the dead end the owner reported.
 * - `arity` — the floor is met, so the line goes back to stating what the animation uses.
 *
 * A rule rather than a ternary in the sheet, for the reason {@link logoPickLabelKey} is one: this
 * repo keeps no UI-component tests, so a condition written into the JSX would be the whole of what
 * the sender is told about a mandatory step, with no coverage at all.
 */
export type TemplateImagesLine =
	| {kind: "invitation"}
	| {kind: "required"; minimum: number}
	| {kind: "arity"; minimum: number; maximum: number};

export function templateImagesLine(
	arity: PersonalizationTemplateArity | null,
	chosenImages: number,
): TemplateImagesLine {
	if (arity === null) {
		return {kind: "invitation"};
	}
	// The MINIMUM is the whole requirement — the same comparison `personalizationDraftError` refuses
	// `tooFewImages` on, so the line and the refusal can never disagree about one draft.
	if (chosenImages < arity.minimumImages) {
		return {kind: "required", minimum: arity.minimumImages};
	}
	return {kind: "arity", minimum: arity.minimumImages, maximum: arity.maximumImages};
}

/**
 * What pressing the builder's Done must do (kitecorp/transferify-ui#2416).
 *
 * - `commit` — arm what was composed and close, which is what Done has always done.
 * - `close` — a rung that may not arm anything (the signed-out preview, a brand that sells none of
 *   this) closes without arming, exactly as before.
 * - `fixImages` — the draft names an animation whose pictures are missing. The press does NOT close:
 *   it puts the sender on the control that fixes it.
 *
 * `fixImages` exists because the owner pressed Done in that state and nothing happened at all. An
 * animation with too few pictures makes `toPersonalizationRequest` answer null, so arming it produced
 * no request for the send or the late attach to carry — a silent no-op wearing the same button as a
 * successful commit.
 *
 * Only `tooFewImages` refuses the press. Every other refusal already reaches the sender twice — the
 * sheet's own alert while it is open, and the panel's send-time answer after it closes (ui#763) — so
 * refusing those as well would add no information and would trap a sender inside the builder over a
 * value they can see.
 */
export type PersonaliseCommitAction = "commit" | "close" | "fixImages";

export function personaliseCommitAction(input: {
	/** The controls answer to this sender: editable, and a commit may arm what they composed. */
	live: boolean;
	error: PersonalizationDraftError | null;
}): PersonaliseCommitAction {
	if (!input.live) {
		return "close";
	}
	return input.error === "tooFewImages" ? "fixImages" : "commit";
}
